import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchUserData } from '../../../api/users/fetchUserData';
import { useAuthContext } from '../../../common/context/AuthContext';
import { BrandOwned } from '../../../common/types';
import SecondaryButton from '../../buttons/SecondaryButton';
import PrimaryHeadline from '../../texts/PrimaryHeadline';
import PrimaryParagraph from '../../texts/PrimaryParagraph';
import PrimaryText from '../../texts/PrimaryText';
import PrimaryTitle from '../../texts/PrimaryTitle';
import { AccountCreationViewProps } from '../types';

const AddItemView: FC<AccountCreationViewProps> = (props) => {
	const { isAuthenticated, isOnboarded, token, uid } = useAuthContext();
	const { t, i18n } = useTranslation();
	const [userItems, setUserItems] = useState<BrandOwned[]>([]);
	const [loadingItems, setLoadingItems] = useState<boolean>(false);
	const [itemsError, setItemsError] = useState<string | null>(null);

	// Log when AddItemView is rendered
	console.debug('🏁 [ADD_ITEM] AddItemView rendered for authenticated user');

	useEffect(() => {
		const loadUserItems = async () => {
			if (!isAuthenticated || !token || !uid) {
				console.debug('🔒 [ADD_ITEM] User not authenticated, skipping items fetch');
				return;
			}
			
			setLoadingItems(true);
			setItemsError(null);
			try {
				console.debug('🔄 [ADD_ITEM] Starting to fetch user items...');
				const userData = await fetchUserData(token, uid, i18n.language);
				setUserItems(userData.brandsOwned || []);
				console.debug('📦 [ADD_ITEM] Successfully loaded user items:', userData.brandsOwned?.length || 0);
			} catch (error) {
				console.error('❌ [ADD_ITEM] Failed to load user items:', error);
				setUserItems([]);
				
				// Set a user-friendly error message
				if (error instanceof Error) {
					if (error.message.includes('Failed to fetch')) {
						setItemsError('network_error');
					} else if (error.message.includes('HTTP error! status: 401')) {
						setItemsError('auth_error');
					} else {
						setItemsError('general_error');
					}
				} else {
					setItemsError('general_error');
				}
			} finally {
				setLoadingItems(false);
			}
		};

		loadUserItems();
	}, [isAuthenticated, token, uid, i18n.language]);

	const handleNext = () => {
		console.debug('🚀 [ADD_ITEM] User clicked "Add an item" button, proceeding to brand selection');
		props.handleNextStep?.();
	};

	return (
		<div className={'pop-h-full pop-fadeIn pop-my-4 pop-flex pop-flex-col pop-border-1'}>
			{/* Block 1: Top Title */}
			<div>
				<PrimaryTitle className="pop-text-center pop-mb-4">
					{t('add_item_title')}
				</PrimaryTitle>
			</div>

			{/* Block 2: Middle Headline + Text + Items List */}
			<div className="pop-flex pop-flex-col pop-items-center pop-flex-1 pop-overflow-hidden">
				<PrimaryHeadline className="pop-text-center pop-text-popsizePrimary20">
					{t('add_item_message_1')}
				</PrimaryHeadline>
				<PrimaryParagraph className="pop-text-center">
					{t('add_item_message_2')}
				</PrimaryParagraph>

				{/* User Items List */}
				{isAuthenticated && (
					<div className="pop-w-full pop-mt-6 pop-flex-1 pop-overflow-hidden">
                        <PrimaryHeadline className="pop-text-center pop-text-sm pop-mb-4">
                            {`${t('your_closet')} (${userItems.reduce((acc, brand) => acc + (brand.items?.length || 0), 0)} ${
                                userItems.reduce((acc, brand) => acc + (brand.items?.length || 0), 0) === 1
                                    ? t('item_singular')
                                    : t('item_plural')
                            })`}
                        </PrimaryHeadline>
						
						{loadingItems && (
							<div className="pop-flex pop-justify-center pop-items-center pop-py-8">
								<span className="pop-loading pop-loading-dots pop-loading-md pop-bg-popsizeGradientSecondary9050"></span>
							</div>
						)}

						{!loadingItems && itemsError && (
							<div className="pop-text-center pop-py-8">
								<PrimaryText className="pop-text-popsizeAccentRed90 pop-mb-2">
									{itemsError === 'network_error' && t('network_error_message')}
									{itemsError === 'auth_error' && t('auth_error_message')}
									{itemsError === 'general_error' && t('items_error_message')}
								</PrimaryText>
								<button 
									onClick={() => window.location.reload()} 
									className="pop-text-sm pop-text-popsizePrimary90 hover:pop-underline"
								>
									{t('retry')}
								</button>
							</div>
						)}

						{!loadingItems && !itemsError && (
							<div className="pop-h-full pop-overflow-y-auto pop-space-y-4">
								{userItems.length === 0 ? (
									<div className="pop-text-center pop-py-8">
										<PrimaryText className="pop-text-popsizePrimary50">
											{t('no_items_yet')}
										</PrimaryText>
									</div>
								) : (
									userItems.map((brand, brandIndex) => (
										<div key={brandIndex} className="pop-my-4">
											{/*<h3 className="pop-text-sm pop-font-semibold pop-text-popsizePrimary90 pop-mb-2">
												{brand.brandLabel} ({brand.numberOfItems} {brand.numberOfItems === 1 ? 'item' : 'items'})
											</h3>*/}
											{brand.items.map((item, itemIndex) => (
												<div key={`${brandIndex}-${itemIndex}`} 
													className="pop-flex pop-items-center pop-justify-center pop-gap-3 pop-bg-popsizePrimary10 pop-border-1 pop-border-popsizePrimary20 pop-rounded-xl pop-px-4 pop-py-2">
													{/* Product Image */}
													{item.productImages && item.productImages[0] ? (
														<img
															src={item.productImages[0]}
															alt={item.productLabel || 'Product'}
															className="pop-w-12 pop-h-12 pop-rounded-xl pop-mr-4"
															onError={(e) => {
																const target = e.target as HTMLImageElement;
																target.style.display = 'none';
															}}
														/>
													) : (
														<div className="pop-w-12 pop-h-12 pop-bg-popsizePrimary10 pop-rounded-md pop-flex pop-items-center pop-justify-center pop-flex-shrink-0">
															<span className="pop-text-xs pop-text-popsizePrimary50">
																{item.brandLabel?.charAt(0) || '?'}
															</span>
														</div>
													)}
													
													{/* Brand Label */}
													<div className=" pop-mr-4 pop-text-sm pop-font-semibold pop-text-popsizePrimary90 pop-min-w-0 pop-flex-shrink-0">
														{item.brandLabel}
													</div>
													
													{/* Product Label */}
													<div className="pop-text-sm pop-text-popsizePrimary70 pop-truncate pop-flex-1">
														{item.productLabel}
													</div>
													
													{/* Size Label */}
													<div className="pop-text-xl pop-font-bold pop-text-popsizePrimary90 pop-flex-shrink-0">
														{item.sizeLabel}
													</div>
												</div>
											))}
										</div>
									))
								)}
							</div>
						)}
					</div>
				)}
			</div>

			{/* Block 3: Bottom Button + Duration */}
			<div className="pop-w-full pop-flex pop-flex-col pop-items-center pop-gap-2 pop-mt-6">
				<SecondaryButton
					className="pop-w-full pop-bg-popsizeGradientSecondary9050 pop-border-none pop-text-white"
					onClick={handleNext}
				>
					{t('add_item_button')}
				</SecondaryButton>
			</div>
		</div>
	);
};

export default AddItemView;
